import type { ApplySuccessPageProps } from '@app/p/apply-success/[token]/[step]/page'
import Button, { buttonClassNames } from '@components/primitives/Button'
import Input from '@components/primitives/Input'
import { classMerge } from '@components/utilities/classMerge'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { powerDateTimeFormat } from '@lib/utilities/date/dateTimeFormats'
import isBefore from 'date-fns/isBefore'
import isEqual from 'date-fns/isEqual'
import { PATIENT_PHONE_CALL_AVAILABILITY_INPUT_NAME } from '../consts'
import { getDateToScheduleCallUTC } from './getDateToScheduleCallUTC'
import type { PhoneCallAvailability } from './types'

export function PhoneCallAvailabilitySelectorPrompt({
  patientTimezone,
  selectedSlots,
  setSelectedSlots,
}: Pick<ApplySuccessPageProps, 'patientTimezone'> & {
  selectedSlots: PhoneCallAvailability[]
  setSelectedSlots: (slots: PhoneCallAvailability[]) => void
}) {
  const dateToScheduleCallUTC = getDateToScheduleCallUTC({ patientTimezone })

  // Show only business hours from 9 AM to 4 PM
  const businessHours = Array.from({ length: 8 }, (_, i) => i + 9) // [9, 10, ..., 16]

  const isSlotSelected = (slot: PhoneCallAvailability) => {
    return Boolean(
      selectedSlots.find((selectedSlot) =>
        isEqual(selectedSlot.startTimeISO8601, slot.startTimeISO8601),
      ),
    )
  }

  const handleAvailabilitySlotClick = (
    dateTimeSlot: Date,
    isPast: boolean,
    isSelected: boolean,
  ) => {
    trackEvent(TrackingEvents.PHONE_CALL_AVAILABILITY_TIME_SLOT_SELECTED, {
      type: isSelected ? 'unselected' : 'selected',
      val: dateTimeSlot.toISOString(),
    })
    if (isPast) return
    if (isSelected) {
      setSelectedSlots(
        selectedSlots.filter(
          (selectedSlot) =>
            !isEqual(selectedSlot.startTimeISO8601, dateTimeSlot),
        ),
      )
    } else {
      setSelectedSlots([...selectedSlots, { startTimeISO8601: dateTimeSlot }])
    }
  }

  return (
    <div className='flex flex-col gap-4 rounded-lg border-2 border-neutral100 bg-white p-6'>
      <div className='flex flex-col gap-2'>
        <h3 className='text-lg font-extrabold leading-[160%] tracking-[-0.18px] text-black'>
          {powerDateTimeFormat(dateToScheduleCallUTC, 'eeee, MMMM d')}
        </h3>
        <p className='text-base font-medium leading-[150%] text-neutral600'>
          Select a time that you would like to receive a reminder tomorrow to
          call the clinic about your application.
        </p>
      </div>
      <div className='show-scrollbar mb-48 grid grid-cols-1 gap-4 overflow-y-scroll md:col-span-1 md:mb-0 md:grid-cols-4 md:overflow-auto'>
        {businessHours.map((hour) => {
          const dateTimeSlot = new Date(dateToScheduleCallUTC)
          dateTimeSlot.setHours(hour, 0, 0, 0) // Set the hour to the current hour in the loop
          const isPast = isBefore(dateTimeSlot, new Date())
          const isSelected = isSlotSelected({
            startTimeISO8601: new Date(dateTimeSlot),
          })

          if (isPast) {
            return <></>
          }
          return (
            <AvailabilitySlot
              dateTimeSlot={dateTimeSlot}
              isPast={isPast}
              isSelected={isSelected}
              key={hour}
              onClick={() => {
                handleAvailabilitySlotClick(dateTimeSlot, isPast, isSelected)
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

function AvailabilitySlot({
  dateTimeSlot,
  isPast,
  isSelected,
  onClick,
}: {
  dateTimeSlot: Date
  isPast: boolean
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <div className='flex w-full items-center justify-center'>
      {isSelected && (
        <Input
          className='hidden'
          name={PATIENT_PHONE_CALL_AVAILABILITY_INPUT_NAME}
          noDefaultStyles
          onChange={() => {}} // To prevent React warning for uncontrolled input (could use defaultValue too)
          tabIndex={-1}
          type='datetime-local'
          value={powerDateTimeFormat(dateTimeSlot, "yyyy-MM-dd'T'HH:mm:ss.SSS")}
        />
      )}
      <Button
        className={classMerge(
          isSelected
            ? buttonClassNames.secondary.purple
            : buttonClassNames.outline.purple,
          'w-full rounded-full border-2 p-3',
        )}
        disabled={isPast}
        onClick={onClick}
      >
        <p className='text-base font-bold leading-[140%] text-primary600'>
          {powerDateTimeFormat(dateTimeSlot, 'haaaaa')}
        </p>
      </Button>
    </div>
  )
}
